import React, {useCallback, useEffect} from 'react';
import {createPortal} from 'react-dom';
import {useReducedMotion} from 'framer-motion';
import {QRCodeCanvas} from 'qrcode.react';
import QrZoomClose from './QrZoomClose';
import QrZoomTicketStatus from './QrZoomTicketStatus';
import * as css from './QrZoom.css';

interface QrZoomProps {
    barcode: string;
    qid: number;
    ticketLabel?: string;
    scanned?: boolean;
    scanTs?: number;
    onClose: () => void;
}

function QrZoom({barcode, qid, ticketLabel, scanned, scanTs, onClose}: QrZoomProps): React.ReactElement {
    const reduceMotion = useReducedMotion();
    const transition = {
        duration: reduceMotion ? 0 : 0.25,
        ease: [0.25, 0.1, 0.25, 1] as const,
    };

    const handleKeyDown = useCallback((e: KeyboardEvent) => {
        if(e.key === 'Escape') {
            onClose();
        }
    }, [onClose]);

    useEffect(() => {
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [handleKeyDown]);

    return createPortal(
        <css.overlay
            initial={{opacity: reduceMotion ? 1 : 0}}
            animate={{opacity: 1}}
            transition={transition}
            onClick={onClose}
            data-cy="qr-zoom"
            role="dialog"
            aria-modal
        >
            <css.inner
                initial={{scale: reduceMotion ? 1 : 0.92}}
                animate={{scale: 1}}
                transition={transition}
                onClick={e => e.stopPropagation()}
            >
                <css.qrWrap>
                    <QRCodeCanvas value={barcode} size={400} />
                </css.qrWrap>
                {ticketLabel && (
                    <css.ticketNo>
                        {ticketLabel} • {qid}
                    </css.ticketNo>
                )}
                <QrZoomTicketStatus scanned={scanned} scanTs={scanTs} />
            </css.inner>
            <QrZoomClose reduceMotion={reduceMotion} onClose={onClose} />
        </css.overlay>,
        document.body,
    ) as React.ReactElement;
}

export default QrZoom;
